/* eslint-disable valid-typeof */

import { maxBy, minBy } from 'lodash';

/**
 * @constant
 * @type {string}
 */
const SEPARATOR = '|';

/**
 * @constant
 * @enum {string}
 */
const BeachTypes = {
  PEBBLE: 'Галька',
  PLATFORM: 'Платформа',
  SAND: 'Песок',
  'SAND+PEBBLE': 'Песок с галькой',
};

/**
 * @constant
 * @enum {string}
 */
const HotelTypes = {
  ACTIVE: 'Активный',
  COUPLES: 'Романтик',
  FAMILY: 'Семейный',
};

/**
 * @constant
 * @enum {string}
 */
const Types = {
  ARRAY: 'array',
  BOOLEAN: 'boolean',
  FUNCTION: 'function',
  NUMBER: 'number',
  OBJECT: 'object',
  OBJECT_LIKE: 'object_like',
  STRING: 'string',
};

/**
 * @constant
 * @enum {string}
 */
const WiFiTypes = {
  ALL: 'Все отели',
  FREE: 'Бесплатный Wi-Fi',
  PAID: 'Есть Wi-Fi',
};

/**
 * @param {*} value
 * @returns {boolean}
 */
const isArray = (value) => Array.isArray(value);

/**
 * @param {*} value
 * @returns {boolean}
 */
const isBoolean = (value) => typeof value === Types.BOOLEAN;

/**
 * @param {*} value
 * @returns {boolean}
 */
const isFunction = (value) => typeof value === Types.FUNCTION;

/**
 * @param {*} value
 * @returns {boolean}
 */
const isNumber = (value) =>
  typeof value === Types.NUMBER && Number.isFinite(value);

/**
 * @param {*} value
 * @returns {boolean}
 */
const isObject = (value) =>
  value && typeof value === Types.OBJECT && value.constructor === Object;

/**
 * @param {*} value
 * @returns {boolean}
 */
const isObjectLike = (value) => typeof value === 'object';

/**
 * @param {*} value
 * @returns {boolean}
 */
const isString = (value) =>
  typeof value === Types.STRING || value instanceof String;

/**
 * Проверяет значение на соответствие типу.
 * Если значение соответствует типу, то возвращается оно же, а иначе -- дефолтное.
 * @param {*} value
 * @param {Types} type
 * @param {*=} defaultValue
 * @returns {*}
 */
const defaultTo = (value, type, defaultValue) => {
  let isSameType = false;
  switch (type) {
    case Types.ARRAY:
      isSameType = isArray(value);
      break;
    case Types.BOOLEAN:
      isSameType = isBoolean(value);
      break;
    case Types.FUNCTION:
      isSameType = isFunction(value);
      break;
    case Types.NUMBER:
      isSameType = isNumber(value);
      break;
    case Types.OBJECT:
      isSameType = isObject(value);
      break;
    case Types.OBJECT_LIKE:
      isSameType = isObjectLike(value);
      break;
    case Types.STRING:
      isSameType = isString(value);
      break;
    default:
      isSameType = false;
      break;
  }

  return isSameType ? value : defaultValue;
};

/**
 * @param {Array} array
 * @param {Function} comparator
 * @returns {*}
 */
const filterOrTakeAll = (array, comparator) => {
  if (!isArray(array)) {
    return null;
  }

  if (!isFunction(comparator)) {
    return array;
  }

  const value = array.filter(comparator);

  return value.length ? value : array;
};

/**
 * @param {Array} lines
 * @returns {string}
 */
const parseSeaLines = (lines) => {
  if (!isArray(lines)) {
    return '';
  }

  const range = [1, 2, 3, 4];
  const selectedLine = lines.find((l) => l.selected);
  if (!selectedLine) {
    return range.join(SEPARATOR);
  }

  return range.slice(0, selectedLine.id - range.length).join(SEPARATOR);
};

/**
 * @param {Array} distances
 * @returns {string}
 */
const parseSkiDistance = (distances) => {
  if (!isArray(distances)) {
    return '';
  }

  const selectedIndex = distances.findIndex((d) => d.selected);
  let selectedIndexes = [];
  if (selectedIndex === -1) {
    selectedIndexes = distances;
  } else {
    selectedIndexes = distances.slice(0, selectedIndex - distances.length);
  }

  return selectedIndexes.map((d) => d.id).join(SEPARATOR);
};

/**
 * @param {Array} stars
 * @returns {string}
 */
const parseStars = (stars) => {
  // Нужен именно такой массив, потому что он отражает реальное состояние фильтров, а фильтра на 1 звезду нет.
  const range = [0, 2, 3, 4, 5];
  let selectedStars = [];
  if (!isArray(stars)) {
    selectedStars = range;
  } else {
    selectedStars = stars.filter((s) => s.selected);
    if (!selectedStars.length) {
      selectedStars = stars;
    }
  }

  return selectedStars.map((s) => s.id).join(SEPARATOR);
};

/**
 * Переводит дату из формата yyyy-mm-dd в UNIX timestamp.
 * @param {string} date
 * @returns {?number}
 */
const parseStartDate = (date) => Math.floor(new Date(date) / 1000) || null;

const parseVariants = (variants) => {
  const parsedOffers = [];

  variants.forEach((room) => {
    // Обходим комнаты.
    if (!isObject(room) || !isArray(room.offers)) {
      return;
    }

    const code = defaultTo(room.code, Types.STRING, '');
    const roomId = defaultTo(`${room.id} `, Types.STRING, '');
    const roomName = defaultTo(room.name, Types.STRING, '');

    room.offers.forEach((offer) => {
      // Обходим типы питания внутри комнаты.
      if (!isObject(offer) || !isArray(offer.filtered_list)) {
        return;
      }

      offer.filtered_list.forEach((pack) => {
        // Обходим предложения внутри типа питания по комнате.
        if (!isObject(pack)) {
          return;
        }

        const extras = defaultTo(pack.extras, Types.OBJECT, {});
        const pansion = defaultTo(pack.pansion, Types.OBJECT, {});

        parsedOffers.push({
          code,
          instant_confirm: defaultTo(
            extras.instant_confirm,
            Types.BOOLEAN,
            false,
          ),
          meal_id: defaultTo(pansion.id, Types.STRING, null),
          meal_name: defaultTo(pansion.description, Types.STRING, ''),
          meal_type: defaultTo(pansion.name, Types.STRING, ''),
          price: defaultTo(pack.price, Types.NUMBER, null),
          operator_id: defaultTo(pack.operator, Types.NUMBER, null),
          opearator_name: defaultTo(pack.operator_name, Types.STRING, ''),
          room_id: roomId,
          room_name: roomName,
          room_type: defaultTo(pack.room_type, Types.STRING, ''),
        });
      });
    });
  });

  return parsedOffers;
};

/**
 * @param {Array} wifis
 * @returns {string}
 */
const parseSelectedWifis = (wifis) => {
  let name = '';
  if (!wifis.length) {
    name = WiFiTypes.ALL;
  } else if (wifis.some((w) => w.id === 'PAID')) {
    name = WiFiTypes.PAID;
  } else {
    name = WiFiTypes.FREE;
  }

  return name;
};

const parsers = {
  // TODO 1: Необходимо заполнить поле reviews_count информацией о количестве отзывов.
  // TODO 2: Необходимо добавить информацию о рейтингах.
  // TODO 3: Необходимо заполнить поле rating информацией о общем рейтинге.
  parseHotelParams: (params) => {
    if (!isObject(params)) {
      return null;
    }

    const hotel = window.HOTEL_DATA
      ? defaultTo(window.HOTEL_DATA.HOTEL, Types.OBJECT, {})
      : {};
    const variants = window.HOTEL_DATA
      ? defaultTo(window.HOTEL_DATA.HOTEL_VARIANTS, Types.ARRAY, [])
      : [];

    const city = defaultTo(hotel.city, Types.OBJECT, {});
    const country = defaultTo(hotel.country, Types.OBJECT, {});
    const departure = defaultTo(params.from, Types.OBJECT, {});
    const isFlexNights = defaultTo(params.flex_nights, Types.BOOLEAN, false);
    const kids = defaultTo(params.kids, Types.ARRAY, []);
    const nights = defaultTo(params.nights, Types.NUMBER, null);
    const startDate = defaultTo(+params.start_date, Types.NUMBER, null);

    return {
      adults_count: defaultTo(params.adults, Types.NUMBER, 0),
      country_id: defaultTo(country.id, Types.NUMBER, null),
      country_name: defaultTo(country.name, Types.STRING, ''),
      departure_name: defaultTo(departure.name, Types.STRING, ''),
      id: defaultTo(hotel.id, Types.NUMBER, null),
      kids_ages: kids.filter((age) => isNumber(age)),
      kids_count: kids.length,
      latitude: defaultTo(+hotel.lat, Types.NUMBER, null),
      longitude: defaultTo(+hotel.long, Types.NUMBER, null),
      name: defaultTo(hotel.name, Types.STRING, ''),
      nights_from: isFlexNights && nights ? nights - 2 : nights,
      nights_to: isFlexNights && nights ? nights + 2 : nights,
      rating: null,
      reviews_count: null,
      stars: defaultTo(hotel.stars, Types.NUMBER, null),
      start_timestamp: startDate ? Math.floor(startDate / 1000) : null,
      to_place_id: defaultTo(city.id, Types.NUMBER, null),
      to_place_name: defaultTo(city.name, Types.STRING, ''),
      variants: parseVariants(variants),
    };
  },

  parseTourSearchImmutableParams: (params) => {
    if (!isObject(params)) {
      return null;
    }

    const {
      filterParams,
      requestId,
      result,
      hotelsCount,
      searchParams,
      searchType,
      sortBy,
      sortOrder,
      originalSortBy,
      status,
      localization,
    } = params;

    let searchFields = {};
    if (isObjectLike(searchParams) && searchParams) {
      const destination = defaultTo(searchParams.to, Types.OBJECT_LIKE, null);
      const dCity = destination
        ? defaultTo(destination.city, Types.OBJECT_LIKE, null)
        : null;
      const dMetaregion = destination
        ? defaultTo(destination.metaregion, Types.OBJECT_LIKE, null)
        : null;
      const dPlace = dCity || dMetaregion;
      const dCountry = destination
        ? defaultTo(destination.country, Types.OBJECT_LIKE, null)
        : null;
      const departure = defaultTo(searchParams.from, Types.OBJECT_LIKE, null);
      const city = departure
        ? defaultTo(departure.city, Types.OBJECT_LIKE, null)
        : null;
      const kids = defaultTo(searchParams.kids, Types.OBJECT_LIKE, null);
      const kidsAges = defaultTo(kids.ages, Types.OBJECT_LIKE, null);
      const nights = defaultTo(searchParams.nights, Types.OBJECT_LIKE, null);
      const options = defaultTo(searchParams.options, Types.OBJECT_LIKE, null);
      const tourIds = searchParams.tour_ids;
      const surchargesByLevel = searchParams.surcharges_by_level;

      searchFields = {
        tour_ids:
          tourIds && defaultTo(tourIds.join(SEPARATOR), Types.STRING, null),
        surcharges_by_level:
          surchargesByLevel &&
          defaultTo(
            surchargesByLevel
              .map((surcharges) => JSON.stringify(surcharges))
              .join(SEPARATOR),
            Types.STRING,
            null,
          ),
        adults_count: defaultTo(searchParams.adults, Types.NUMBER, 0),
        country_id: dCountry
          ? defaultTo(dCountry.id, Types.NUMBER, null)
          : null,
        country_name: dCountry
          ? defaultTo(dCountry.name_en, Types.STRING, '')
          : '',
        departure_id: city ? defaultTo(city.id, Types.NUMBER, null) : null,
        departure_name: city ? defaultTo(city.name_en, Types.STRING, '') : '',
        hotel_ids:
          searchParams.hotel_ids &&
          defaultTo(searchParams.hotel_ids.join(SEPARATOR), Types.STRING, null),
        accommodation_availability:
          searchParams.accommodation_availability &&
          defaultTo(
            searchParams.accommodation_availability.join(SEPARATOR),
            Types.STRING,
            null,
          ),
        flight_seats:
          searchParams.flight_seats &&
          defaultTo(
            searchParams.flight_seats.join(SEPARATOR),
            Types.STRING,
            null,
          ),
        hotel_names: '',
        is_flex_start_date: options
          ? !!defaultTo(options.flex_dates, Types.NUMBER, 0)
          : false,
        kids_ages: kidsAges
          ? defaultTo(
              kidsAges.filter((age) => isNumber(age)).join(SEPARATOR),
              Types.STRING,
              '',
            )
          : '',
        kids_count: kidsAges ? defaultTo(kidsAges.size, Types.NUMBER, 0) : 0,
        nights_from: nights ? defaultTo(nights.from, Types.NUMBER, 0) : 0,
        nights_to: nights ? defaultTo(nights.to, Types.NUMBER, null) : null,
        request_id: defaultTo(requestId, Types.STRING, null),
        start_timestamp: parseStartDate(searchParams.start_date),
        to_place_id: defaultTo(dPlace.id, Types.NUMBER, null),
        to_place_name: defaultTo(dPlace.name_en, Types.STRING, ''),
        search_type: defaultTo(searchType, Types.STRING, ''),
        currency_code: defaultTo(localization.currencyCode, Types.STRING, ''),
        bundle_ids:
          searchParams.bundle_ids &&
          defaultTo(searchParams.bundle_ids.join(','), Types.STRING, null),
        acm_component_ids:
          searchParams.acm_component_ids &&
          defaultTo(
            searchParams.acm_component_ids.join(','),
            Types.STRING,
            null,
          ),
      };
    }

    let filterFields;
    if (isObjectLike(filterParams)) {
      const beachTypes = defaultTo(
        filterParams.get('beach_type'),
        Types.OBJECT_LIKE,
        null,
      );
      const beaches = defaultTo(
        filterParams.get('beach_surface'),
        Types.OBJECT_LIKE,
        null,
      );
      const confirms = defaultTo(
        filterParams.get('instant_confirm'),
        Types.OBJECT_LIKE,
        null,
      );
      const hotelQuery = defaultTo(
        filterParams.get('hotel_name'),
        Types.OBJECT_LIKE,
        null,
      );
      const hotelTypes = defaultTo(
        filterParams.get('category'),
        Types.OBJECT_LIKE,
        null,
      );
      const designatedBeach = beachTypes
        ? beachTypes.find((type) => type.get('id') === 'DESIGNATED')
        : null;
      const meals = defaultTo(
        filterParams.get('meals'),
        Types.OBJECT_LIKE,
        null,
      );
      const operators = defaultTo(
        filterParams.get('operators'),
        Types.OBJECT_LIKE,
        null,
      );
      const prices = defaultTo(
        filterParams.get('prices'),
        Types.OBJECT_LIKE,
        null,
      );
      const rating = defaultTo(
        filterParams.get('rating'),
        Types.OBJECT_LIKE,
        null,
      );
      const regions = defaultTo(
        filterParams.get('regions'),
        Types.OBJECT_LIKE,
        null,
      );
      const seaLines = defaultTo(
        filterParams.get('line'),
        Types.OBJECT_LIKE,
        null,
      );
      const selectedBeaches = beaches
        ? filterOrTakeAll(beaches.toJS(), (b) => b.selected)
        : null;
      const selectedConfirm = confirms
        ? confirms.find((c) => c.get('selected'))
        : null;
      const selectedHotelTypes = hotelTypes
        ? filterOrTakeAll(hotelTypes.toJS(), (b) => b.selected)
        : null;
      const selectedMeals = meals
        ? filterOrTakeAll(meals.toJS(), (m) => m.selected)
        : null;
      const selectedOperators = operators
        ? filterOrTakeAll(operators.toJS(), (o) => o.selected)
        : null;
      const selectedRating = rating
        ? rating.find((r) => r.get('selected'))
        : null;
      const selectedRegions = regions
        ? filterOrTakeAll(regions.toJS(), (r) => r.selected)
        : null;
      const skiDistance = defaultTo(
        filterParams.get('ski_distance'),
        Types.OBJECT_LIKE,
        null,
      );
      const stars = defaultTo(
        filterParams.get('stars'),
        Types.OBJECT_LIKE,
        null,
      );
      const wifis = defaultTo(
        filterParams.get('wifi'),
        Types.OBJECT_LIKE,
        null,
      );
      const selectedWifis = wifis
        ? filterOrTakeAll(wifis.toJS(), (w) => w.selected)
        : null;

      filterFields = {
        airport_distance: '',
        beach_designated: designatedBeach
          ? defaultTo(designatedBeach.get('selected'), Types.BOOLEAN, false)
          : false,
        beach_ids: selectedBeaches
          ? selectedBeaches.map((b) => b.id).join(SEPARATOR)
          : '',
        beach_names: selectedBeaches
          ? selectedBeaches.map((b) => BeachTypes[b.id]).join(SEPARATOR)
          : '',
        budget_from: prices
          ? defaultTo(
              prices.get('minPrice') || prices.get('analytics_min'),
              Types.NUMBER,
              0,
            )
          : 0,
        budget_to: prices
          ? defaultTo(
              prices.get('maxPrice') || prices.get('analytics_max'),
              Types.NUMBER,
              null,
            )
          : null,
        hotel_types: selectedHotelTypes
          ? selectedHotelTypes.map((t) => t.id).join(SEPARATOR)
          : '',
        hotel_types_ids: selectedHotelTypes
          ? selectedHotelTypes.map((t) => HotelTypes[t.id]).join(SEPARATOR)
          : '',
        hotel_query: hotelQuery
          ? defaultTo(hotelQuery.get('value'), Types.STRING, '')
          : '',
        instant_confirm: selectedConfirm ? selectedConfirm.get('id') : false,
        meals_ids: selectedMeals
          ? selectedMeals.map((m) => m.id).join(SEPARATOR)
          : '',
        meals_names: selectedMeals
          ? selectedMeals.map((m) => m.name).join(SEPARATOR)
          : '',
        operators_ids: selectedOperators
          ? selectedOperators.map((o) => o.id).join(SEPARATOR)
          : '',
        operators_names: selectedOperators
          ? selectedOperators.map((o) => o.name).join(SEPARATOR)
          : '',
        rating_from: selectedRating
          ? defaultTo(selectedRating.get('id'), Types.NUMBER, 0)
          : 0,
        rating_to: 10,
        region_ids: selectedRegions
          ? selectedRegions.map((r) => r.id).join(SEPARATOR)
          : '',
        region_names: selectedRegions
          ? selectedRegions.map((r) => r.name).join(SEPARATOR)
          : '',
        scenario_ids: '',
        scenario_names: '',
        sea_lines: seaLines ? parseSeaLines(seaLines.toJS()) : '',
        ski_distance: skiDistance ? parseSkiDistance(skiDistance.toJS()) : '',
        stars: stars ? parseStars(stars.reverse().toJS()) : '',
        wifi_ids: selectedWifis
          ? selectedWifis.map((w) => w.id).join(SEPARATOR)
          : null,
        wifi_names: selectedWifis ? parseSelectedWifis(selectedWifis) : '',
      };
    }

    let resultFields;
    if (result) {
      const maxPriceHotel = maxBy(result, 'analytics_max_price');
      const minPriceHotel = minBy(result, 'analytics_min_price');

      resultFields = {
        result_count: defaultTo(hotelsCount, Types.NUMBER, 0),
        result_maxprice: maxPriceHotel
          ? defaultTo(maxPriceHotel.analytics_max_price, Types.NUMBER, null)
          : null,
        result_minprice: minPriceHotel
          ? defaultTo(minPriceHotel.analytics_min_price, Types.NUMBER, null)
          : null,
        result_minprices: result
          .map((item) => item.analytics_min_price)
          .join(SEPARATOR),
        result_surcharges: result
          .map((item) => item.analytics_surcharge)
          .join(SEPARATOR),
        instant_confirm_labels: result
          .map((hotel) => hotel.instantConfirm)
          .join(SEPARATOR),
        labels: result
          .map((hotel) => {
            const { labels } = hotel;
            if (!labels) return null;

            return labels.map((label) => label.id).join(';');
          })
          .join(SEPARATOR),
        confirmability_index: result
          .map((hotel) => hotel.confirmability_index)
          .join(SEPARATOR),
      };
    }

    const sortFields = {
      sorting: defaultTo(sortBy, Types.STRING, ''),
      sorting_order: defaultTo(sortOrder, Types.STRING, ''),
      originalSortBy: defaultTo(originalSortBy, Types.STRING, ''),
    };

    const statusFields = {
      result_operators: status ? status.join(SEPARATOR) : '',
      result_statuses: status ? status.join(SEPARATOR) : '',
    };

    return {
      ...searchFields,
      ...filterFields,
      ...resultFields,
      ...sortFields,
      ...statusFields,
    };
  },
};

export default parsers;
